// components/MasterListBase.jsx - Shared list/table/cards for All/Active/Inactive Masters
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Smartphone, Search, Eye, Download, Edit, UserCheck, UserX, Cpu, Loader2, AlertTriangle, Ban } from 'lucide-react';
import { Panel, StatusDot, Pagination, SummaryCards, EmptyState, timeAgo, initials, gradFor } from '../Masters/UiElements';
import { getAllMasters } from '../Masters/AdminAPI';
import { canView, canEdit, canExport } from '../../view/Permissions';
import axios from 'axios';
import { usePermissions } from '../../view/Permissions';
import { toast } from 'react-toastify';
import { API_BASE, getHeaders } from '../../config/api';

const API_BASE_URL = `${API_BASE}/api`;

const PAGE_SIZE = 5;

// ---- CSV export helpers ----
const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

const exportMastersToCSV = (masters, filename = 'All Masters') => {
    if (!masters || masters.length === 0) return;

    const columns = [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phoneNumber', label: 'Phone' },
        { key: 'planStatus', label: 'Plan' },
        { key: 'maxDevicesAllowed', label: 'Max Devices' },
        { key: 'masterStatus', label: 'Status' },
        { key: 'lastLogin', label: 'Last Login' },
    ];

    const header = columns.map((c) => escapeCsvValue(c.label)).join(',');
    const rows = masters.map((m) =>
        columns
            .map((c) => {
                let val = m[c.key];
                if (c.key === 'lastLogin' && val) {
                    try {
                        val = new Date(val).toLocaleString();
                    } catch {
                        // keep raw value if parsing fails
                    }
                }
                return escapeCsvValue(val ?? '');
            })
            .join(',')
    );

    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.setAttribute('download', `${filename}_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
// ---- end CSV export helpers ----

const MasterCard = ({ master, onView, onEdit }) => (
    <Panel className="p-4 space-y-3 hover:shadow-lg transition-all">
        <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradFor(master.email)} flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-lg`}>
                {initials(master.name)}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate text-gray-800">{master.name}</p>
                <p className="text-xs truncate text-gray-500">{master.email}</p>
            </div>
            <StatusDot status={master.status} />
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
            {[
                { val: master.maxDevicesAllowed ?? 0, label: 'Max Devices' },
                { val: master.planStatus || '—', label: 'Plan' },
                { val: timeAgo(master.lastLogin), label: 'Last login' },
            ].map((item) => (
                <div key={item.label} className="rounded-xl py-2 bg-orange-50 border border-orange-200">
                    <p className="text-xs font-bold text-orange-500 capitalize">{item.val}</p>
                    <p className="text-[10px] text-gray-500">{item.label}</p>
                </div>
            ))}
        </div>
        <div className="flex items-center justify-end gap-1.5 pt-1 border-t border-orange-200">
            <button onClick={() => onView(master._id)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-xl transition-all bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100 hover:scale-105">
                <Eye size={13} /> View
            </button>
            <button onClick={() => onEdit(master._id)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-xl transition-all bg-blue-50 text-blue-500 border border-blue-200 hover:bg-blue-100 hover:scale-105">
                <Edit size={13} /> Edit
            </button>
        </div>
    </Panel>
);


const MasterListBase = ({ statusFilter, title, subtitle }) => {
    const navigate = useNavigate();
    const [masters, setMasters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);
    const [PageName, setPageName] = useState('All Masters');

    useEffect(() => {
        if (statusFilter === 'all') {
            setPageName('All Masters');
        } else if (statusFilter === 'active') {
            setPageName('Active Masters');
        } else
            setPageName('Inactive Masters');
    }, [statusFilter]);

    const {
        canView,
        canAdd,
        canEdit,
        canDelete,
        canDownload,
        canExport
    } = usePermissions();

    const loadMasters = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getAllMasters(statusFilter);
            setMasters(res.masters || []);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to load masters');
            toast.error(err?.response?.data?.message || err.message || 'Failed to load masters');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        loadMasters();
    }, [loadMasters]);

    const filtered = masters.filter((m) => {
        const q = search.toLowerCase();
        return (
            (m.name || '').toLowerCase().includes(q) ||
            (m.email || '').toLowerCase().includes(q) ||
            (m.phoneNumber || '').toLowerCase().includes(q)
        );
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const summary = [
        { label: 'Total Masters', value: filtered.length, icon: Users, grad: '' },
        { label: 'Active', value: masters.filter((m) => m.masterStatus === 'active').length, icon: UserCheck, grad: '' },
        { label: 'Inactive', value: masters.filter((m) => m.masterStatus === 'inactive').length, icon: UserX, grad: '' },
        { label: 'Total Connected Devices', value: masters.reduce((t, m) => t + (m.registeredDevices?.length || 0), 0), icon: Smartphone, grad: '' },
    ];

    const handleView = (id) => navigate(`/admin/master/view/${id}`);
    const handleEdit = (id) => navigate(`/admin/master/edit/${id}`);

    const handleExport = () => {
        if (filtered.length === 0) return;
        setExporting(true);
        try {
            const filenameBase = statusFilter ? `masters_${statusFilter}` : 'masters_all';
            exportMastersToCSV(filtered, filenameBase);
            toast.success('Export completed successfully!');
        } catch (error) {
            toast.error('Failed to export data. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const toggleMasterStatus = async (masterId, currentStatus) => {
        try {
            const newStatus =
                currentStatus === "active" ? "inactive" : "active";

            const token = sessionStorage.getItem("adminToken") || sessionStorage.getItem("subAdminToken") || "";

            const response = await axios.put(
                `${API_BASE_URL}/superAdmin/userDocs/update-master-status`,
                {
                    masterId,
                    status: newStatus,
                },
                {
                    headers: getHeaders(),
                }
            );

            toast.success(`Master status updated to ${newStatus} successfully!`);

            return {
                success: true,
                statusCode: response.status,
                data: response.data,
                newStatus,
            };
        } catch (error) {
            console.error(
                "Master status update failed:",
                error.response?.data || error.message
            );

            toast.error(error.response?.data?.message || 'Failed to update master status. Please try again.');

            return {
                success: false,
                statusCode: error.response?.status || 500,
                data: error.response?.data,
                error: error.message,
            };
        }
    };

    return (
        <div className="space-y-6 bg-gray-50 min-h-screen p-4 rounded-2xl">
            <div className="flex items-center justify-between flex-wrap gap-2 bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div>
                    <h1 className="text-xl font-bold text-gray-800">{title}</h1>
                    <p className="text-sm mt-0.5 text-gray-500">{subtitle}</p>
                </div>
                <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-gray-50 border border-orange-200 text-gray-600">
                    <Cpu size={12} className="text-orange-500" />
                </div>
            </div>

            <SummaryCards items={summary} />

            <Panel className="p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, or phone…"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-white border border-orange-200 text-gray-800 focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                        />
                    </div>
                    {canExport(PageName) && (
                        <button
                            onClick={handleExport}
                            disabled={exporting || filtered.length === 0}
                            className="flex items-center justify-center gap-2 px-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            <span className="hidden sm:inline">Export</span>
                        </button>
                    )}
                </div>
            </Panel>

            {error && (
                <Panel className="p-4 flex items-center gap-2 text-sm text-red-500 bg-red-50 border-red-200">
                    <AlertTriangle size={16} /> {error}
                </Panel>
            )}

            {loading ? (
                <Panel className="p-16 flex items-center justify-center text-orange-400">
                    <Loader2 size={28} className="animate-spin" />
                </Panel>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {paginated.length === 0 ? (
                            <EmptyState icon={Users} label="No masters match your filters" />
                        ) : (
                            paginated.map((m) => <MasterCard key={m._id} master={m} onView={handleView} onEdit={handleEdit} />)
                        )}
                    </div>

                    {/* Desktop table */}
                    <Panel className="hidden md:block overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-orange-200 bg-orange-50">
                                        {['Admin', 'Phone', 'Plan', 'Max Devices', 'Status', 'Last Login', 'Actions'].map((h) => (
                                            <th key={h} className="text-left text-xs font-semibold px-5 py-3.5 text-gray-600">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="text-center py-16 text-gray-500">
                                                <div className="flex flex-col items-center gap-2">
                                                    <Users size={32} className="opacity-30" />
                                                    <p className="text-sm">No masters match your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginated.map((m, idx) => (
                                            <tr key={m._id} className={`${idx < paginated.length - 1 ? 'border-b border-orange-100' : ''} hover:bg-orange-50 transition-colors`}>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradFor(m.email)} flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-md`}>
                                                            {initials(m.name)}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold text-gray-800">{m.name}</p>
                                                            <p className="text-xs text-gray-500">{m.email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-600">{m.phoneNumber}</td>
                                                <td className="px-4 py-4 text-sm text-gray-600 capitalize">{m.planStatus || '—'}</td>
                                                <td className="px-4 py-4 text-center text-sm text-orange-500 font-semibold">{m.maxDevicesAllowed ?? 0}</td>
                                                {canEdit(PageName) ? (
                                                    <td className="px-4 py-4 text-start">
                                                        <StatusDot
                                                            status={m.masterStatus}
                                                            onClick={async () => {
                                                                const result = await toggleMasterStatus(
                                                                    m._id,
                                                                    m.masterStatus
                                                                );

                                                                if (result.success) {

                                                                    setMasters((prevMasters) =>
                                                                        prevMasters.map((master) =>
                                                                            master._id === m._id
                                                                                ? {
                                                                                    ...master,
                                                                                    masterStatus: result.newStatus,
                                                                                }
                                                                                : master
                                                                        )
                                                                    );
                                                                } else {
                                                                    console.error(
                                                                        "Master update failed:",
                                                                        result.statusCode
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                ) : (
                                                    <td className="px-4 py-4 text-start">
                                                        {m.status === 'active' ? 'Active' : 'Inactive'}
                                                    </td>
                                                )}
                                                <td className="px-4 py-4 text-xs text-gray-500">{timeAgo(m.lastLogin)}</td>
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center gap-1.5">
                                                        {canView(PageName) ? (
                                                            <button onClick={() => handleView(m._id)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100">
                                                                <Eye size={13} />
                                                            </button>
                                                        ) : (
                                                            <div className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all bg-gray-100 text-gray-400 border border-gray-200">
                                                                <Ban size={13} className="bg-gray-100 text-red-500" /> no access
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="px-5 py-3 flex items-center justify-between border-t border-orange-200 bg-orange-50">
                            <p className="text-xs text-gray-600">
                                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} masters
                            </p>
                            <Pagination current={page} total={totalPages} onChange={setPage} />
                        </div>
                    </Panel>

                    <Panel className="md:hidden px-4 py-3 flex items-center justify-between">
                        <p className="text-xs text-gray-600">
                            {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                        </p>
                        <Pagination current={page} total={totalPages} onChange={setPage} />
                    </Panel>
                </>
            )}
        </div>
    );
};

export default MasterListBase;